import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, SquareMenu } from 'lucide-react';
import { toast } from 'sonner';

export default function MenuCategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', display_order: 0, available_for_room_service: false });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await api.get('/menu-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/menu-categories/${editingCategory.id}`, formData);
        toast.success('Category updated');
      } else {
        await api.post('/menu-categories', formData);
        toast.success('Category created');
      }
      setDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', display_order: 0 });
      loadCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order || 0,
      available_for_room_service: category.available_for_room_service || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/menu-categories/${id}`);
      toast.success('Category deleted');
      loadCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await api.put(`/menu-categories/${category.id}`, { is_active: !category.is_active });
      toast.success(`Category ${category.is_active ? 'deactivated' : 'activated'}`);
      loadCategories();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openNewDialog = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', display_order: 0, available_for_room_service: false });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="menu-categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Menu Categories</h1>
          <p className="text-gray-500 text-sm mt-1">Manage restaurant menu categories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} className="bg-gradient-orange hover:opacity-90 text-white btn-glow" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Main Course, Appetizers"
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  className="border-orange-200 focus:border-orange-400"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="border-orange-200 focus:border-orange-400"
                />
              </div>
              <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                <Switch
                  checked={formData.available_for_room_service}
                  onCheckedChange={(checked) => setFormData({ ...formData, available_for_room_service: checked })}
                  data-testid="category-room-service-switch"
                />
                <div>
                  <Label className="text-violet-800 font-medium">Available for Room Service</Label>
                  <p className="text-xs text-violet-600">Items in this category will be shown in Guest Portal</p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange hover:opacity-90 text-white" data-testid="save-category-btn">
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-orange-100 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <SquareMenu className="h-5 w-5 text-orange-500" />
            All Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No categories found. Add your first category!</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-orange-100">
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Room Service</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id} className="border-orange-100 table-row-hover">
                    <TableCell className="font-medium text-gray-800">{category.name}</TableCell>
                    <TableCell className="text-gray-500">{category.description || '-'}</TableCell>
                    <TableCell>{category.display_order}</TableCell>
                    <TableCell>
                      <Switch
                        checked={category.available_for_room_service || false}
                        onCheckedChange={async (checked) => {
                          try {
                            await api.put(`/menu-categories/${category.id}`, { available_for_room_service: checked });
                            toast.success(`Room service ${checked ? 'enabled' : 'disabled'}`);
                            loadCategories();
                          } catch (error) {
                            toast.error('Failed to update');
                          }
                        }}
                        data-testid={`room-service-${category.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={category.is_active}
                          onCheckedChange={() => handleToggleActive(category)}
                          data-testid={`toggle-${category.id}`}
                        />
                        <span className={category.is_active ? 'text-emerald-600 text-sm' : 'text-gray-400 text-sm'}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(category)} className="text-gray-500 hover:text-orange-600">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(category.id)} className="text-gray-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
